/**
 * Booking from inside the chat (owner, 2026-10-12: "the requirements of
 * reservation via ai are the same on booking on website ... based on the item
 * ... and possibility to show packages").
 *
 * The chat used to propose (C10) and send the guest to the place's booking
 * sheet for everything else. It now asks the sheet's questions itself, one card
 * per question (lab: pewter-chat-kit-lab, C31 to C40), and sends the request
 * through the same checkout the sheet uses.
 *
 * Everything that decides a price or a payload is the sheet's own rule, read
 * from the shared packages, never re-derived here:
 *   - `calcTotal` (per person, per hour, per night, per vehicle + sharing)
 *   - `pickupState` (offered, free, tiered, too soon)
 *   - `bookingSubmissionCore` (a table sends no package and no total)
 *   - `takesMultiplePacks` (a spa counts treatments) + the guest-mix note
 *   - `guestCapacity`, `minAdultsForGroups`, `normalizeMinPeople`
 * The server re-prices and re-checks every booking (notice, past times, stop
 * sales, minimum party, spa rule), so a stale card is refused with the same
 * localized message the sheet shows.
 *
 * Kept out on purpose (they still open the place's own form): nightclubs (their
 * own night-and-table sheet), transfers (the ride card books them) and paused
 * venues. Hotels and riads are not proposals: they are paid online from the
 * stays list.
 */
import {
  bookingSubmissionCore,
  formatGuestMixLine,
  isFullName,
  isNightclubBooking,
  normalizeMinPeople,
  paysByLink,
  pickupState,
  splitFullName,
  stripGuestMixLine,
  takesMultiplePacks,
} from "@majourneys/shared";
import { calcTotal, guestCapacity, minAdultsForGroups } from "@majourneys/booking";
import type { BookingProposal } from "../api/ai.api";

export type ChatPackage = {
  id: string;
  name: string;
  description?: string | null;
  timeStart?: string | null;
  maxGuests?: number | null;
  includedGuests?: number | null;
  typePrice?: 0 | 1 | 2 | 3 | null;
  pricing: {
    adult: number;
    child: number;
    infant: number;
    transport: number | null;
    transportLarge?: number | null;
    transportOutside?: number | null;
    extraGuest?: number | null;
  };
};

export type ChatActivity = {
  id: string;
  slug: string;
  title: string;
  template?: string | null;
  typePrice?: 0 | 1 | 2 | 3 | null;
  segment?: string | null;
  category?: { slug: string } | null;
  cityName?: string | null;
  minPeople?: number | null;
  bookingPaused?: boolean | null;
  packages: ChatPackage[];
};

/** How this place books. Decides which cards the chat asks. */
export type ChatBookingKind = "table" | "multi" | "single";

export function chatBookingKind(a: ChatActivity): ChatBookingKind {
  if (a.template === "restaurant") return "table";
  if (takesMultiplePacks(a.category?.slug)) return "multi";
  return "single";
}

/** Places whose booking stays on their own form, and why. */
export function handOffReason(
  a: ChatActivity,
): "nightclub" | "transfer" | "paused" | "no_packages" | null {
  if (a.bookingPaused) return "paused";
  if (a.category?.slug === "transport") return "transfer";
  if (
    isNightclubBooking({
      categorySlug: a.category?.slug ?? null,
      segment: a.segment ?? null,
      packages: a.packages as never,
    })
  )
    return "nightclub";
  if (chatBookingKind(a) !== "table" && a.packages.length === 0) return "no_packages";
  return null;
}

export type ChatBookingState = {
  packageId: string | null;
  /** Spas: treatments chosen, by package id. */
  packs: Record<string, number>;
  adults: number;
  children: number;
  infants: number;
  /** Per vehicle (typePrice 3). */
  groups: number;
  /** Per hour (typePrice 1). */
  hours: number;
  pickup: boolean;
  pickupOutside: boolean;
  pickupAddress: string;
  fullName: string;
  phone: string;
  email: string;
  notes: string;
  /** Spas: who is coming, from the proposal (the AI checked the spa rule). */
  women: number | null;
  men: number | null;
};

/** The state the cards open on, from what the AI already agreed. */
export function initialChatBooking(
  a: ChatActivity,
  p: BookingProposal,
  user?: { name?: string | null; email?: string | null },
): ChatBookingState {
  const kind = chatBookingKind(a);
  const minParty = normalizeMinPeople(a.minPeople);
  const proposed = a.packages.some((x) => x.id === p.packageId) ? p.packageId! : null;
  const packageId = kind === "table" ? null : (proposed ?? (a.packages.length === 1 ? a.packages[0]!.id : null));
  const party = Math.max(1, p.adults + p.children);
  return {
    packageId,
    // A treatment the guest named is counted for the whole party, as the sheet does.
    packs: kind === "multi" && proposed ? { [proposed]: party } : {},
    adults: Math.max(minParty, p.adults || 1),
    children: Math.max(0, p.children || 0),
    infants: 0,
    groups: 1,
    hours: 1,
    pickup: false,
    pickupOutside: false,
    pickupAddress: "",
    fullName: (user?.name ?? "").trim(),
    phone: "",
    email: (user?.email ?? "").trim(),
    notes: "",
    women: p.women ?? null,
    men: p.men ?? null,
  };
}

export function selectedPackage(a: ChatActivity, s: ChatBookingState): ChatPackage | null {
  return a.packages.find((x) => x.id === s.packageId) ?? null;
}

export function typePriceOf(a: ChatActivity, s: ChatBookingState): 0 | 1 | 2 | 3 {
  const pkg = selectedPackage(a, s);
  return ((pkg?.typePrice ?? a.typePrice ?? 0) as 0 | 1 | 2 | 3);
}

export function packLines(a: ChatActivity, s: ChatBookingState) {
  return a.packages
    .map((pkg) => ({ pkg, quantity: s.packs[pkg.id] ?? 0 }))
    .filter((l) => l.quantity > 0);
}

/** Who pays, as the sheet counts it: a spa's party IS its treatments. */
export function partySize(a: ChatActivity, s: ChatBookingState): number {
  if (chatBookingKind(a) === "multi") return packLines(a, s).reduce((n, l) => n + l.quantity, 0);
  return s.adults + s.children + s.infants;
}

/**
 * Pickup as the sheet reads it. An included (free) pickup is simply on, as the
 * sheet ticks it for the guest: the driver still needs an address.
 */
export function pickupFor(a: ChatActivity, s: ChatBookingState, tooSoon = false) {
  const pkg = selectedPackage(a, s);
  const read = (selected: boolean) =>
    pickupState({
      transport: chatBookingKind(a) === "single" ? pkg?.pricing.transport : null,
      transportLarge: pkg?.pricing.transportLarge ?? null,
      transportOutside: pkg?.pricing.transportOutside ?? null,
      paidGuests: partySize(a, s),
      outside: s.pickupOutside,
      selected,
      requirePaidPickup: false,
      tooSoon,
    });
  const free = read(false).free;
  return read(s.pickup || free);
}

/** The total in dirhams, as the server will price it. 0 for a table. */
export function chatBookingTotal(a: ChatActivity, p: BookingProposal, s: ChatBookingState, tooSoon = false): number {
  const kind = chatBookingKind(a);
  if (kind === "table") return 0;
  if (kind === "multi") return packLines(a, s).reduce((n, l) => n + Math.max(0, l.pkg.pricing.adult) * l.quantity, 0);
  const pkg = selectedPackage(a, s);
  if (!pkg) return 0;
  const tp = typePriceOf(a, s);
  const pick = pickupFor(a, s, tooSoon);
  return calcTotal(
    tp,
    pkg as never,
    { adults: s.adults, children: s.children, infants: s.infants },
    tp === 1 ? s.hours : 1,
    pick.applied,
    tp === 2 ? (p.nights ?? 1) : 0,
    tp === 3 ? s.groups : 1,
    pick.outsideApplied,
  );
}

/** Seats one booking can hold, 0 when the venue sets no cap. */
export function capacityFor(a: ChatActivity, s: ChatBookingState): number {
  const pkg = selectedPackage(a, s);
  return guestCapacity(typePriceOf(a, s), pkg?.maxGuests, s.groups, pkg as never);
}

export type ChatBookingStep = "package" | "guests" | "details" | "review";

/** What is still missing, in the order the cards ask it. Empty: ready to send. */
export function missingSteps(a: ChatActivity, s: ChatBookingState, tooSoon = false): ChatBookingStep[] {
  const kind = chatBookingKind(a);
  const out: ChatBookingStep[] = [];
  if (kind === "single" && !s.packageId) out.push("package");
  if (kind === "multi" && packLines(a, s).length === 0) out.push("package");
  if (!guestsComplete(a, s, tooSoon)) out.push("guests");
  if (!detailsComplete(s)) out.push("details");
  return out;
}

export function guestsComplete(a: ChatActivity, s: ChatBookingState, tooSoon = false): boolean {
  const minParty = normalizeMinPeople(a.minPeople);
  if (chatBookingKind(a) === "multi") {
    const n = partySize(a, s);
    if (n < 1) return false;
    // The mix the AI agreed must still cover every treatment.
    if (s.women != null && s.men != null && s.women + s.men !== n) return false;
    return true;
  }
  // Who is coming depends on the package (its seats, its pickup): asked after it.
  if (chatBookingKind(a) === "single" && !s.packageId) return false;
  if (s.adults + s.children < minParty) return false;
  if (typePriceOf(a, s) === 3 && s.adults < minAdultsForGroups(s.groups)) return false;
  const cap = capacityFor(a, s);
  if (cap > 0 && s.adults + s.children + s.infants > cap) return false;
  // A pickup (paid, or included and so always on) needs somewhere to go.
  if (pickupFor(a, s, tooSoon).applied && !s.pickupAddress.trim()) return false;
  return true;
}

export const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function phoneOk(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  return /^\+/.test(phone.trim()) && digits.length >= 8;
}

export function detailsComplete(s: ChatBookingState): boolean {
  return isFullName(s.fullName) && phoneOk(s.phone) && EMAIL_SHAPE.test(s.email.trim());
}

/** How this booking is paid, for the review's total line. */
export function paymentLine(a: ChatActivity): "free" | "arrival" | "link" {
  if (chatBookingKind(a) === "table") return "free";
  if (paysByLink(a.slug)) return "link";
  return "arrival";
}

export type ChatCheckoutPayload = {
  activityId: string;
  activitySlug: string;
  packageId?: string;
  packs?: { packageId: string; quantity: number }[];
  date: string;
  dateEnd?: string;
  time?: string;
  adults: number;
  children: number;
  infants: number;
  hours?: number;
  nights?: number;
  groups?: number;
  transport: boolean;
  transportOutside?: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  transportAddress?: string;
  paymentMethod: "arrival";
  notes?: string;
  total: number;
  lang?: string;
};

/** The checkout body, built exactly as the booking sheet builds it. */
export function chatCheckoutPayload(
  a: ChatActivity,
  p: BookingProposal,
  s: ChatBookingState,
  lang: string,
  tooSoon = false,
): ChatCheckoutPayload {
  const kind = chatBookingKind(a);
  const tp = typePriceOf(a, s);
  const pick = pickupFor(a, s, tooSoon);
  const lines = packLines(a, s);
  const multi = kind === "multi";
  const core = bookingSubmissionCore({
    isTableFlow: kind === "table",
    packageId: multi ? lines[0]?.pkg.id : (s.packageId ?? undefined),
    adults: multi ? Math.max(1, partySize(a, s)) : s.adults,
    children: multi ? 0 : s.children,
    infants: multi ? 0 : s.infants,
    transport: pick.applied,
    transportOutside: pick.outsideApplied,
    total: chatBookingTotal(a, p, s, tooSoon),
  });
  const { firstName, lastName } = splitFullName(s.fullName);
  const mix =
    multi && s.women != null && s.men != null ? formatGuestMixLine({ women: s.women, men: s.men }) : undefined;
  const typed = stripGuestMixLine(s.notes).trim();
  const notes = [mix, typed].filter(Boolean).join("\n") || undefined;
  return {
    activityId: a.id,
    activitySlug: a.slug,
    packageId: core.packageId,
    packs: multi ? lines.map((l) => ({ packageId: l.pkg.id, quantity: l.quantity })) : undefined,
    date: p.date.slice(0, 10),
    dateEnd: tp === 2 && p.checkOut ? p.checkOut.slice(0, 10) : undefined,
    time: p.time || undefined,
    adults: core.adults,
    children: core.children,
    infants: core.infants,
    hours: tp === 1 ? s.hours : undefined,
    nights: tp === 2 ? (p.nights ?? 1) : undefined,
    groups: tp === 3 ? s.groups : undefined,
    transport: core.transport,
    transportOutside: core.transportOutside ?? false,
    firstName,
    lastName,
    phone: s.phone.trim(),
    email: s.email.trim(),
    transportAddress: pick.applied ? s.pickupAddress.trim() || undefined : undefined,
    paymentMethod: "arrival",
    notes,
    total: core.total,
    lang,
  };
}
